// Päivitysilmoituksen tila (Androidin APK-sovellus): hakee palvelimelta julkaistun version ja vertaa sitä
// asennettuun. Verkkoselaimessa (ei natiivisovellus) ei tehdä mitään, koska selain päivittyy palvelimelta itse.
// Puhdas logiikka on lib/appUpdate.js:ssä; tämä tiedosto liittää sen Capacitoriin ja käyttöliittymän tilaan.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";

import { getServerSettings, isNativeApp } from "./api.js";
import { checkDue, downloadUrl, installedBuild, shouldPrompt, updateAvailable } from "./appUpdate.js";
import { storage } from "./storage.js";

/** Asennetun version tiedot Androidilta: { build, version } tai null (selain / epäonnistui). */
async function readInstalled() {
  try {
    const { App } = await import("@capacitor/app");
    return await App.getInfo();
  } catch {
    return null;
  }
}

/**
 * @returns {{ supported, installed, remote, available, prompt, checking, error, check, dismiss, download }}
 *   prompt = näytetään päivitysikkuna (uusi versio, jota ei ole ohitettu). check(true) = käyttäjän oma tarkistus.
 */
export function useAppUpdate(api) {
  const supported = isNativeApp();
  const [installed, setInstalled] = useState(null); // { build, version }
  const [remote, setRemote] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const lastCheck = useRef(NaN);
  const installedRef = useRef(null);

  const check = useCallback(
    async (manual = false) => {
      if (!supported) return;
      if (!manual && !checkDue(lastCheck.current, Date.now())) return;
      lastCheck.current = Date.now();
      setChecking(true);
      setError(null);
      try {
        const info = installedRef.current ?? (await readInstalled());
        installedRef.current = info;
        setInstalled(info);
        const data = await api.appVersion();
        setRemote(data);
        if (updateAvailable(info, data) && shouldPrompt(data.build, storage.get("updateDismissed", null))) setPrompt(true);
      } catch (e) {
        // Hiljaa taustalla: verkko voi puuttua. Vain käyttäjän oma tarkistus näyttää virheen.
        if (manual) setError(e);
      }
      setChecking(false);
    },
    [api, supported],
  );

  useEffect(() => {
    if (!supported) return undefined;
    check(false);
    let handle;
    import("@capacitor/app")
      .then(({ App }) => App.addListener("resume", () => check(false)))
      .then((h) => (handle = h))
      .catch(() => {});
    return () => handle?.remove?.();
  }, [supported, check]);

  const dismiss = () => {
    if (remote?.build) storage.set("updateDismissed", remote.build); // vasta uudempi julkaisu kysyy uudelleen
    setPrompt(false);
  };

  // Selain hoitaa latauksen; Android pyytää asennuksen vahvistuksen lopuksi.
  const download = () => {
    window.open(downloadUrl(getServerSettings().baseUrl, remote?.download), "_blank");
    setPrompt(false);
  };

  return {
    supported,
    installed,
    installedBuild: installedBuild(installed),
    remote,
    available: updateAvailable(installed, remote),
    prompt,
    checking,
    error,
    check,
    dismiss,
    download,
  };
}
